import { el } from "../utils/dom";

export interface ConfirmOptions {
  title: string;
  message: string | HTMLElement;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

let host: HTMLElement | null = null;

/** Mount the single global confirm-dialog host. Call once from the app shell. */
export function mountConfirmHost(container: HTMLElement): void {
  host = el("div", { id: "confirm-host" });
  container.append(host);
}

/** Collect all tabbable elements within a container (excludes disabled). */
function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
  ).filter((node) => !node.hasAttribute("disabled"));
}

/**
 * Confirm destructive/expensive actions (disable, revoke, trigger). Resolves
 * true if the operator confirmed, false if cancelled/dismissed.
 */
export function confirmAction(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    if (!host) {
      // No host mounted (shouldn't happen) - fail closed rather than skip confirmation.
      resolve(false);
      return;
    }

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const titleId = `confirm-title-${Date.now()}`;

    // eslint-disable-next-line prefer-const
    let overlay: HTMLElement;

    const close = (result: boolean) => {
      overlay.remove();
      document.removeEventListener("keydown", onKeydown);
      resolve(result);
      // Restore focus to the element that was active before the dialog opened.
      previouslyFocused?.focus();
    };

    const onKeydown = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        ev.preventDefault();
        close(false);
        return;
      }
      if (ev.key === "Tab") {
        const focusable = getFocusable(dialog);
        if (!focusable.length) {
          ev.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (ev.shiftKey) {
          if (document.activeElement === first) {
            ev.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            ev.preventDefault();
            first.focus();
          }
        }
      }
    };

    const cancelBtn = el(
      "button",
      { className: "btn btn-ghost", onclick: () => close(false) },
      [options.cancelLabel ?? "Cancel"]
    );

    const confirmBtn = el(
      "button",
      {
        className: options.danger ? "btn btn-danger-ghost" : "btn btn-primary",
        onclick: () => close(true),
      },
      [options.confirmLabel ?? "Confirm"]
    );

    const dialog = el(
      "div",
      {
        className: "dialog",
        role: "alertdialog",
        "aria-modal": "true",
        "aria-labelledby": titleId,
        tabIndex: -1,
      },
      [
        el("div", { className: "dialog__title", id: titleId }, [options.title]),
        el("div", { className: "dialog__message" }, [options.message]),
        el("div", { className: "dialog__actions" }, [cancelBtn, confirmBtn]),
      ]
    );

    overlay = el(
      "div",
      {
        className: "overlay overlay--confirm",
        onclick: (ev: MouseEvent) => {
          if (ev.target === overlay) close(false);
        },
      },
      [dialog]
    );

    document.addEventListener("keydown", onKeydown);
    host.append(overlay);

    // Initial focus: Cancel button (safer default - prevents accidental confirmation).
    // For non-destructive dialogs this still makes sense; Tab moves to Confirm.
    cancelBtn.focus();
  });
}
